import { deleteRole, getRoleList } from "api/userManagement";
import DeleteRole from "components/Modal/DeleteRole";
import Permission from "components/Modal/Permission";
import RoleManagementModal from "components/Modal/RoleManagementModal";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { BackArrow } from "themes/Icon";
import ButtonComp from "themes/elements/Button";
import RolesTable from "themes/elements/Table/RolesTable";
import { retrieveUserData } from "utils/cryptoStorage";
import dateUtils from "utils/dateUtils";
import { isPermit } from "utils/permissionCheck";

import RoleDetails from "components/Modal/RoleDetails";
import {
  clearPermissionSettings,
  setCurrentPageNo,
  setEditRole,
  setLoading,
  setPermissionByCategory,
  setRoleDeleteLoading,
  setRoleDeleteModalOpen,
  setRolesData,
  setRolesDataDropdown,
  setRolesPermissionViewOnly,
  setSelectedPermissions,
  setSelectedRole,
  setTotalPageNo,
  togglePermissionModal,
  toggleRoleModal,
} from "./roleSlice";

function ManageRoles() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [filterRole, setFilterRole] = useState();
  const [filterDesc, setFilterDesc] = useState("");
  const [details, setDetails] = useState(false);

  const [filterUserCount, setFilterUserCount] = useState(null);

  const [filterCreatedDate, setFilterCreatedDate] = useState(null);

  const [filterStatus, setFilterStatus] = useState({ value: 2, name: "All" });

  const userToken = retrieveUserData();
  let admin_user_id, admin_user_name;

  if (userToken) {
    ({ admin_user_id, admin_user_name } = userToken);
  }
  const {
    rolesData,
    isLoading,
    roleModal,
    selectedRole,
    permissionModal,
    editRole,
    currentPageNo,
  } = useSelector((state) => state.roles);

  const RolesColumnData = [
    {
      title: "Role",
      key: "role_name",
      filter: "selectRole",
      dropDown: ["Super Admin", "General Admin", "Councillor"],
      width: 258,
    },
    {
      title: "Number of users",
      key: "no_of_users",
      filter: "filterRange",
      width: 140,
    },
    {
      title: "Role description",
      key: "role_description",
      filter: "searchDesc",
      width: 318,
    },
    {
      title: "Created on",
      key: "created_date",
      filter: "datePicker",
      width: 175,
    },
    {
      title: "Status",
      key: "status",
      filter: "selectStatus",
      dropDown: [
        { value: 2, name: "All" },
        { value: 1, name: "Active" },
        { value: 0, name: "Inactive" },
      ],
      chip: true,
      width: 175,
    },
  ];

  useEffect(() => {
    fetchRoles();
  }, [
    filterRole,
    filterStatus,
    filterDesc,
    filterCreatedDate,
    filterUserCount,
    currentPageNo,
  ]);

  useEffect(() => {
    dispatch(setCurrentPageNo(1));
  }, []);

  useEffect(() => {
    fetchRolesDropDown();
  }, []);

  const fetchRoles = async () => {
    dispatch(setLoading(true));
    try {
      const response = await getRoleList({
        page: currentPageNo,
        perPage: 10,
        status: filterStatus.value,
        roleId: filterRole,
        roleDescription: filterDesc,
        createdDate: dateUtils.formatDateToYYYMMDD(filterCreatedDate),
        minUsers: filterUserCount ? filterUserCount[0] : null,
        maxUsers: filterUserCount ? filterUserCount[1] : null,
      });

      dispatch(setRolesData(response.roleList));
      if (currentPageNo === 1) {
        dispatch(setTotalPageNo(response?.meta?.last_page * 10));
      }
    } catch (error) {}
    dispatch(setLoading(false));
  };

  const fetchRolesDropDown = async () => {
    dispatch(setLoading(true));
    try {
      const response = await getRoleList({
        page: currentPageNo,
        perPage: 100,
        status: 2,
      });

      dispatch(setRolesDataDropdown(response.roleList));
    } catch (error) {}
    dispatch(setLoading(false));
  };

  const handleDeleteRole = async (data) => {
    dispatch(setRoleDeleteLoading(true));
    try {
      const response = await deleteRole({
        role_id: Number(data?.role_id),
      });
      if (response) {
        toast.success(response.message);
        dispatch(setRoleDeleteModalOpen(false));
        dispatch(setRoleDeleteLoading(true));
        dispatch(setRoleDeleteLoading(false));

        fetchRoles();
        // Refetch roles after successful deletion
      }
    } catch (error) {
      dispatch(setRoleDeleteLoading(false));
    } finally {
      dispatch(setRoleDeleteLoading(false));
    }
  };
  const handleEdit = (data) => {
    dispatch(setSelectedRole(data));

    // Group permissions based on module_id
    const groupedPermissions = {
      roleManagement: [],
      userManagement: [],
      notificationManagement: [],
      newsManagement: [],
      wardManagement: [],
      incidentManagement: [],
      counsilorManagement: [],
      projectManagement: [],
    };

    data?.permissions.forEach((permission) => {
      switch (permission?.module_id) {
        case 1:
          groupedPermissions.roleManagement.push(permission.permission_name);
          break;
        case 2:
          groupedPermissions.userManagement.push(permission.permission_name);
          break;
        case 3:
          groupedPermissions.newsManagement.push(permission.permission_name);
          break;
        case 4:
          groupedPermissions.notificationManagement.push(
            permission.permission_name
          );
          break;
        case 5:
          groupedPermissions.wardManagement.push(permission.permission_name);
          break;
        case 6:
          groupedPermissions.incidentManagement.push(
            permission.permission_name
          );
          break;
        case 7:
          groupedPermissions.counsilorManagement.push(
            permission.permission_name
          );
          break;
        case 8:
          groupedPermissions.projectManagement.push(permission.permission_name);
          break;
        default:
          break;
      }
    });

    // Dispatch updates for each category
    dispatch(
      setPermissionByCategory({
        category: "roleManagement",
        permissions: groupedPermissions.roleManagement,
      })
    );
    dispatch(
      setPermissionByCategory({
        category: "userManagement",
        permissions: groupedPermissions.userManagement,
      })
    );
    dispatch(
      setPermissionByCategory({
        category: "notificationManagement",
        permissions: groupedPermissions.notificationManagement,
      })
    );
    dispatch(
      setPermissionByCategory({
        category: "newsManagement",
        permissions: groupedPermissions.newsManagement,
      })
    );

    dispatch(
      setPermissionByCategory({
        category: "wardManagement",
        permissions: groupedPermissions.wardManagement,
      })
    );
    dispatch(
      setPermissionByCategory({
        category: "incidentManagement",
        permissions: groupedPermissions.incidentManagement,
      })
    );
    dispatch(
      setPermissionByCategory({
        category: "counsilorManagement",
        permissions: groupedPermissions.counsilorManagement,
      })
    );
    dispatch(
      setPermissionByCategory({
        category: "projectManagement",
        permissions: groupedPermissions.projectManagement,
      })
    );

    const permissionsForDispatch = data.permissions.map(
      (permission) => permission.permission_name
    );
    dispatch(setSelectedPermissions(permissionsForDispatch));
    dispatch(toggleRoleModal(true));
    dispatch(setEditRole(true));
  };

  return (
    <>
      <div className="page-wrapper flex flex-column gap-2">
        <div className="flex wrap gap-2 align-center justify-between">
          <div className="flex align-center gap-4">
            <span
              onClick={() => navigate("/user-management")}
              className="pointer"
            >
              <BackArrow />
            </span>
            <h2 className="page-title">Manage Roles</h2>
          </div>
          {isPermit(1, "create") && (
            <ButtonComp
              onClick={() => {
                dispatch(setEditRole(false));
                dispatch(setRolesPermissionViewOnly(false));
                dispatch(clearPermissionSettings());
                dispatch(toggleRoleModal(true));
              }}
            >
              Add Role
            </ButtonComp>
          )}
        </div>
        {isPermit(1, "view") && (
          <RolesTable
            setFilterUserCount={setFilterUserCount}
            filterUserCount={filterUserCount}
            filterCreatedDate={filterCreatedDate}
            setFilterCreatedDate={setFilterCreatedDate}
            setFilterDesc={setFilterDesc}
            filterDesc={filterDesc}
            filterRole={filterRole}
            setFilterRole={setFilterRole}
            dataClick={(data) => {
              dispatch(setSelectedRole(data));
              setDetails(true);
            }}
            filterStatus={filterStatus}
            setFilterStatus={setFilterStatus}
            column={RolesColumnData}
            data={rolesData}
            currentPageNo={currentPageNo}
            handleEdit={handleEdit}
            onDeleteAction={(data) => {
              dispatch(setSelectedRole(data));
              dispatch(setRoleDeleteModalOpen(true));
            }}
            editAction
            isLoading={isLoading}
          />
        )}
        <RoleManagementModal
          fetchRoles={fetchRoles}
          data={selectedRole}
          isEdit={editRole}
          setOpen={(value) => dispatch(toggleRoleModal(value))}
          open={roleModal}
        />
        <Permission
          fetchRoles={fetchRoles}
          isEdit={editRole}
          setOpen={(value) => dispatch(togglePermissionModal(value))}
          open={permissionModal}
        />
        <DeleteRole
          selectedData={selectedRole}
          data={selectedRole}
          handleDeleteRole={handleDeleteRole}
        />
        <RoleDetails
          // setEdit={setEditUser}
          open={details}
          setOpen={setDetails}
        />
      </div>
    </>
  );
}

export default ManageRoles;
